import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import { TOAST_MESSAGES } from "~/constants/messages";
import { OrganizationApi } from "~/functions/api/organization";
import { UserApi } from "~/functions/api/user";
import AddWhiteIcon from "~/images/add_white_icon.svg?react";
import ContextMenu from "~/images/context_menu.svg?react";
import { removeGuestAction } from "~/slices/organizationSlice";
import type { RootState } from "~/store";
import InviteGuestModal from "../inviteGuestModal";
import styles from "./index.module.scss";

const getRoleDisplayText = (role: string): string => {
  const roleText: Record<string, string> = {
    guest: "ゲスト",
  };
  return roleText[role] || role;
};

// Helper function to check if a guest can be deleted
// Combines all deletion permission checks in one place:
// 1. Only admin can delete users (not manager, member, or guest)
// 2. Cannot delete self
const canDeleteUser = (
  guestId: string,
  currentUser: { id?: string; role?: string } | undefined,
): boolean => {
  if (!currentUser) return false;
  // Permission: only admin can delete
  if (currentUser.role !== "admin") return false;
  // Self-deletion prevention
  if (guestId === currentUser.id) return false;
  return true;
};

export const GuestList: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const guests = useSelector((state: RootState) => state.organization.guests);
  const dispatch = useDispatch();

  // Get max limits from organization plan
  const maxGuests = organization?.plan?.limits?.maxGuests || 0;

  const sortedGuests = useMemo(() => {
    return [...guests].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [guests]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Only admin can see and use the context menu (for delete)
  const isAdmin = currentUser?.role === "admin";

  const handleRemoveUser = async (userId: string) => {
    const userApi = new UserApi();
    const response = await userApi.delete(currentUser?.organization_id, userId);
    if (response.success) {
      dispatch(removeGuestAction(userId));
      toast.success(TOAST_MESSAGES.ORGANIZATION.DELETE_USER_SUCCESS);
    } else {
      // エラーメッセージがAPIから伝播される
      toast.error(response.error);
    }
    setDropdownOpen(null);
  };

  const handleResendInvite = async (userId: string) => {
    const organizationApi = new OrganizationApi();
    const response = await organizationApi.sendInviteEmail(currentUser?.organization_id, userId);
    if (response.success) {
      toast.success(TOAST_MESSAGES.ORGANIZATION.RESEND_INVITE_SUCCESS);
    } else {
      // エラーメッセージがAPIから伝播される
      toast.error(response.error);
    }
  };

  return (
    <div className={styles["listContent"]}>
      <div className="flex justify-between items-center w-full font-sans">
        <h1>ゲスト</h1>
        <div className="flex-end flex gap-[4px] flex-col">
          {currentUser.role !== "guest" && (
            <CommonButton
              onClick={() => setModalOpen(true)}
              className="w-[136px]"
              labelClassName="!px-0"
              icon={<AddWhiteIcon />}
              disabled={sortedGuests.length >= maxGuests}
              data-testid="add-guest-button"
            >
              ゲスト追加
            </CommonButton>
          )}
          <span className="text-[11px] text-gray">
            利用可能ゲスト: {`${guests?.length || 0}/${maxGuests}`}
          </span>
        </div>
      </div>

      <ul className={styles["memberList"]}>
        {sortedGuests?.map((guest, index) => (
          <li className={styles["memberItem"]} key={guest.id}>
            <div className={styles["memberAvatar"]}>
              <AccountCircleIcon style={{ fontSize: 40, color: "#1D9FD6" }} />
            </div>
            <div className={styles["memberDetails"]}>
              <span className={styles["memberInfo"]}>{guest.display_name}</span>
              {guest.status === "invited" ? (
                <div className={styles["invitedMemberInfo"]}>
                  <span className={styles["memberRole"]}>招待中</span>
                  <Button
                    label="招待メール再送信"
                    styleType="white-tiny"
                    onClick={() => handleResendInvite(guest.id)}
                  />
                </div>
              ) : (
                <div className={styles["memberRole"]}>
                  <span>{getRoleDisplayText(guest.role)}</span>
                </div>
              )}
            </div>
            {isAdmin && (
              <div
                className={styles["moreOptions"]}
                ref={index === dropdownOpen ? dropdownRef : null}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(dropdownOpen === index ? null : index);
                  }}
                >
                  <ContextMenu />
                </button>

                {dropdownOpen === index && (
                  <div className={styles["dropdownMenu"]}>
                    <ul>
                      {canDeleteUser(guest.id, currentUser) && (
                        <li
                          className={styles["danger"]}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveUser(guest.id);
                          }}
                        >
                          削除
                        </li>
                      )}
                      <li
                        className={styles["cancelOption"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen(null);
                        }}
                      >
                        キャンセル
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* ゲスト招待モーダル */}
      <InviteGuestModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};
